import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Checkbox, Divider, Tag, Typography } from 'antd';
import { useAtomValue } from 'jotai';
import { METRIC_POPUP_OK_TEXT, isAppModule, METRIC_TYPE_MAP } from '@utils/constants';
import { activationCreateAccessSelector } from '@store/common/selectors';
import { useRefTags } from '@src/hooks';
import { tid } from '@utils/test';
import { categoryMetaAtom, categoryMetaOrderAtom, configAtom, flowDataAtom } from '../atoms';
import ModalTitleWithSearch from './ModalTitleSearch';
import ModalAnchor from './MetricAnchor';
import MetricSelectorRightPanel from './MetricSelectorRightPanel';
import {
  LoadingBar,
  LoadingStrip,
  ContentWrapper,
  RightPanel,
  FooterWrapper,
  CountText,
  FlexFill,
  StyledDrawer,
  StyledLeftPanel,
  StyledTitleHeader,
  StyledNoFlow,
  StyledTitle,
} from '../styled';
import NewCustomMetricBox from './NewCustomMetricBox';
import { noop } from 'lodash';

const MetricPopupModal = ({
  allowDimensionApply = false,
  applyToDimensions = false,
  onApplyToDimensionsChange = noop,
  applyDisabled = false,
  title,
  visible,
  onCancel,
  onOk,
  okText = METRIC_POPUP_OK_TEXT,
  onClear,
  lockedMetricsSet,
  metricCategories,
  onChange,
  onBulkChange,
  searchRef,
  isLoading = false,
  selectedMetricList,
}) => {
  const productCategoryMeta = useAtomValue(categoryMetaAtom);
  const productCategoryOrder = useAtomValue(categoryMetaOrderAtom);
  const metricConfig = useAtomValue(configAtom);
  const { refs, setRefFn } = useRefTags(productCategoryOrder);
  const [searchText, setSearchText] = useState('');
  const isECOContext = isAppModule;
  const hasActivationCreateAccess: any = useSelector(activationCreateAccessSelector);
  const inIframe = window.location !== window.parent.location; // if app is opened in iframe inside emp.
  const shouldShowFlow = isECOContext && metricConfig.showFlow;

  const { flowMetricCategoryOrder, flowMetricCategoryMeta } = useAtomValue(flowDataAtom);

  const onCancelModal = () => {
    setSearchText('');
    onCancel();
  };

  const onCreate = (key) => {
    if (key === METRIC_TYPE_MAP.FLOW) {
      window.open(`/app/activation/workflow/flow/new`, '_blank');
    } else if (key === METRIC_TYPE_MAP.METRIC) {
      window.open(`/app/activation/workflow/metric/new`, '_blank');
    }
  };

  const metricCount = selectedMetricList.length;
  const footerButtons = [
    <FooterWrapper key="footer-wrapper" {...tid('footer')}>
      <Button key="clear" onClick={onClear}>
        {allowDimensionApply ? 'Clear selection' : 'Clear All'}
      </Button>
      <Button key="discard" onClick={onCancelModal}>
        Cancel
      </Button>
      <FlexFill />
      <CountText>{`${metricCount} ${metricCount > 1 ? 'metrics' : 'metric'} selected`}</CountText>
      {allowDimensionApply && (
        <div style={{ marginInline: 12 }}>
          <Checkbox
            checked={applyToDimensions}
            onChange={(event) => onApplyToDimensionsChange(event.target.checked)}
          >
            Apply to dimension tables{' '}
            <Tag color="blue" style={{ marginInlineEnd: 0 }}>
              New
            </Tag>
          </Checkbox>
          {applyToDimensions && metricCount > 20 && (
            <Typography.Text type="danger" style={{ display: 'block' }}>
              Select at most 20 metrics to apply to dimension tables.
            </Typography.Text>
          )}
        </div>
      )}
      <Button type="primary" key="save" disabled={applyDisabled} onClick={onOk}>
        {okText}
      </Button>
    </FooterWrapper>,
  ];

  const titleWithSearch = (
    <ModalTitleWithSearch
      placeholder="Search metric"
      modalTitle={title}
      searchText={searchText}
      setSearchText={setSearchText}
      searchRef={searchRef}
    />
  );

  const showCreateMetricButton =
    metricConfig.createCustomMetric && isECOContext && hasActivationCreateAccess && !inIframe;

  return (
    <StyledDrawer
      title={titleWithSearch}
      open={visible}
      onClose={onCancelModal}
      footer={footerButtons}
      width={1024}
      destroyOnHidden
      {...tid('metric-popup')}
    >
      <ContentWrapper {...tid('content')}>
        <StyledLeftPanel>
          <>
            <StyledTitleHeader>
              <StyledTitle>
                <h3>Metrics</h3>
              </StyledTitle>
              {showCreateMetricButton ? <NewCustomMetricBox onCreate={onCreate} /> : null}
            </StyledTitleHeader>
            <ModalAnchor
              navRefs={refs.current}
              categoryOrder={productCategoryOrder}
              listCategory={productCategoryMeta}
            />
          </>
          {shouldShowFlow && (
            <>
              <Divider />
              <StyledTitleHeader>
                <StyledTitle>
                  <h3>Flows</h3>
                </StyledTitle>
              </StyledTitleHeader>
              {flowMetricCategoryMeta && Object.keys(flowMetricCategoryMeta).length === 0 ? (
                <StyledNoFlow>No flow available</StyledNoFlow>
              ) : null}
              <ModalAnchor
                navRefs={refs.current}
                categoryOrder={flowMetricCategoryOrder}
                listCategory={flowMetricCategoryMeta}
              />
            </>
          )}
        </StyledLeftPanel>
        <RightPanel>
          {isLoading ? (
            <LoadingStrip>
              <LoadingBar />
            </LoadingStrip>
          ) : null}
          <MetricSelectorRightPanel
            searchText={searchText}
            selectedMetricList={selectedMetricList}
            lockedMetricsSet={lockedMetricsSet}
            onChange={onChange}
            onBulkChange={onBulkChange}
            setRefFn={setRefFn}
            metricCategories={metricCategories}
            shouldShowFlow={shouldShowFlow}
          />
        </RightPanel>
      </ContentWrapper>
    </StyledDrawer>
  );
};

export default MetricPopupModal;
